import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Table, TableStatus, ApiResponse } from '@/types'
import { RootState } from '@/app/store'
import { getAuthToken } from '@/utils/secureStorage'

// Base API configuration
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    // Try to get token from Redux state first
    let token = (getState() as RootState).auth.token
    
    // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
    if (!token) {
      token = getAuthToken()
    }
    
    const language = (getState() as RootState).settings.settings.language
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    
    headers.set('lang', language)
    headers.set('Content-Type', 'application/json')
    headers.set('Accept', 'application/json')
    
    return headers
  },
})

// Tables API slice
export const tablesApi = createApi({
  reducerPath: 'tablesApi',
  baseQuery,
  tagTypes: ['Tables'],
  endpoints: (builder) => ({
    // Get all tables
    getTables: builder.query<ApiResponse<Table[]>, void>({
      query: () => '/tables',
      providesTags: ['Tables'],
    }),
    
    // Get table details
    getTableDetails: builder.query<ApiResponse<Table>, string | number>({
      query: (tableId) => `/tables/${tableId}`,
      providesTags: (_result, _error, id) => [{ type: 'Tables', id }],
    }),
    
    // Update table status
    updateTableStatus: builder.mutation<ApiResponse<Table>, { tableId: string | number; status: TableStatus }>({
      query: ({ tableId, status }) => ({
        url: `/tables/${tableId}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Tables'],
    }),
    
    // Assign order to table
    assignOrderToTable: builder.mutation<ApiResponse<Table>, { tableId: string | number; orderId: string | number }>({
      query: ({ tableId, orderId }) => ({
        url: `/tables/${tableId}/assign-order`,
        method: 'PUT',
        body: { order_id: orderId },
      }),
      invalidatesTags: ['Tables'],
    }),
    
    // Clear table
    clearTable: builder.mutation<ApiResponse<Table>, string | number>({
      query: (tableId) => ({
        url: `/tables/${tableId}/clear`,
        method: 'PUT',
      }),
      invalidatesTags: ['Tables'],
    }),
  }),
})

export const {
  useGetTablesQuery,
  useGetTableDetailsQuery,
  useUpdateTableStatusMutation,
  useAssignOrderToTableMutation,
  useClearTableMutation,
} = tablesApi
